"use client";

import { useEffect, useMemo, useRef, useState } from "react";
import Vapi from "@vapi-ai/web";

import {
  Mic,
  Video,
  Monitor,
  PhoneOff,
  Clock3,
  FileText,
  MessageSquare,
  ScrollText,
  ChevronLeft,
  ChevronRight,
} from "lucide-react";

// ============================================
// IMPORTANT
// ============================================
//
// 1. UPDATE VAPI PROMPT
//
// VERY IMPORTANT:
//
// Your Vapi assistant prompt should be:
//
// --------------------------------------------
//
// You are the RealHired AI Interview Voice Assistant.
//
// IMPORTANT RULES:
//
// - You DO NOT control interview flow.
// - You DO NOT generate your own interview questions.
// - You ONLY speak messages provided by the external interview engine.
// - Wait silently after speaking.
// - Never continue interview on your own.
// - Never ask your own questions.
// - Never evaluate answers.
//
// Your ONLY job:
// 1. Speak provided text
// 2. Listen to user
// 3. Return transcript
//
// --------------------------------------------
//
// ============================================

const INTERVIEW_TYPES = [
  {
    value: "frontend",
    label: "Frontend Engineering",
  },

  {
    value: "backend",
    label: "Backend Engineering",
  },

  {
    value: "lld",
    label: "Low Level Design",
  },

  {
    value: "hld",
    label: "High Level Design",
  },
];

// ============================================
// QUESTIONS
// ============================================

const QUESTION_BANK = {
  frontend: [
    "Introduce yourself",

    "Tell me about your frontend experience",

    "Explain React reconciliation",

    "How would you optimize a slow React application?",

    "Design a scalable frontend architecture for a dashboard system",
  ],

  backend: [
    "Introduce yourself",

    "Tell me about your backend experience",

    "Explain database indexing",

    "How would you scale a Node.js API?",

    "Design a notification microservice",
  ],

  lld: [
    "Introduce yourself",

    "Design an LLD for Parking Lot System",

    "Explain SOLID principles",

    "How would you make your design extensible?",

    "How would you handle concurrency?",
  ],

  hld: [
    "Introduce yourself",

    "Design YouTube at high level",

    "How would you scale uploads?",

    "How would you handle recommendations?",

    "Explain caching strategy",
  ],
};

export default function AiInterviewPage() {
  // ============================================
  // REFS
  // ============================================

  const vapiRef = useRef(null);

  const evaluationTimeoutRef = useRef(null);

  const lastEvaluatedPayloadRef =
    useRef("");

  // ============================================
  // STATES
  // ============================================

  const [status, setStatus] =
    useState("idle");

  const [error, setError] =
    useState(null);

  const [interviewType, setInterviewType] =
    useState("");

  const [messages, setMessages] =
    useState([]);

  const [answerDoc, setAnswerDoc] =
    useState("");

  const [timeLeft, setTimeLeft] =
    useState(15 * 60);

  const [activeTab, setActiveTab] =
    useState("doc");

  const [collapsed, setCollapsed] =
    useState(false);

  const [evaluation, setEvaluation] =
    useState(null);

  const [evaluating, setEvaluating] =
    useState(false);

  const [waitingForUser, setWaitingForUser] =
    useState(false);

  const [questionIndex, setQuestionIndex] =
    useState(0);

  const [currentQuestion, setCurrentQuestion] =
    useState("");

  // ============================================
  // TIMER
  // ============================================

  useEffect(() => {
    if (status !== "running") return;

    const timer = setInterval(() => {
      setTimeLeft((prev) => {
        if (prev <= 1) {
          clearInterval(timer);

          return 0;
        }

        return prev - 1;
      });
    }, 1000);

    return () => clearInterval(timer);
  }, [status]);

  // ============================================
  // INIT VAPI
  // ============================================

  useEffect(() => {
    vapiRef.current = new Vapi(
      "2a1257fe-4713-435b-847a-f138e1f34d7e"
    );

    // CALL START

    vapiRef.current.on("call-start", () => {
      console.log("CALL STARTED");

      setStatus("running");
    });

    // CALL END

    vapiRef.current.on("call-end", () => {
      console.log("CALL ENDED");

      setStatus("ended");
    });

    // TRANSCRIPTS

    vapiRef.current.on("message", (msg) => {
      if (
        msg.type === "transcript" &&
        msg.transcriptType === "final"
      ) {
        console.log("TRANSCRIPT", msg);

        setMessages((prev) => [
          ...prev,
          {
            role: msg.role,
            text: msg.transcript,
            timestamp: Date.now(),
          },
        ]);

        // USER ANSWERED
        if (msg.role === "user") {
          setWaitingForUser(false);
        }
      }
    });

    vapiRef.current.on("error", (err) => {
      console.error(err);

      setError("Interview failed");
    });

    return () => {
      vapiRef.current?.stop();
    };
  }, []);

  // ============================================
  // START INTERVIEW
  // ============================================

  const startInterview = async () => {
    try {
      if (!interviewType) {
        setError(
          "Please select interview type"
        );

        return;
      }

      setError(null);

      setStatus("loading");

      // START VAPI

      await vapiRef.current.start(
        "fbd5a8d0-7ab9-43c6-aa6d-8736065a0625"
      );

      // FIRST QUESTION

      const firstQuestion =
        QUESTION_BANK[interviewType][0];

      setCurrentQuestion(firstQuestion);

      setQuestionIndex(0);

      setTimeout(async () => {
        await askQuestion(
          firstQuestion
        );
      }, 2000);

    } catch (err) {
      console.error(err);

      setError(
        "Failed to start interview"
      );

      setStatus("idle");
    }
  };

  // ============================================
  // ASK QUESTION
  // ============================================

  const askQuestion = async (
    question
  ) => {
    try {
      setWaitingForUser(true);

      setCurrentQuestion(question);

      setMessages((prev) => [
        ...prev,
        {
          role: "assistant",
          text: question,
          timestamp: Date.now(),
        },
      ]);

      await vapiRef.current.say(
        question
      );

    } catch (err) {
      console.error(err);
    }
  };

  // ============================================
  // STOP INTERVIEW
  // ============================================

  const stopInterview = async () => {
    try {
      await vapiRef.current.stop();

      setStatus("ended");
    } catch (err) {
      console.error(err);
    }
  };

  // ============================================
  // EVALUATION LOOP
  // ============================================

  useEffect(() => {
    // AI WAITING
    if (waitingForUser) return;

    const lastMessage =
      messages[messages.length - 1];

    // ONLY USER
    if (
      !lastMessage ||
      lastMessage.role !== "user"
    ) {
      return;
    }

    clearTimeout(
      evaluationTimeoutRef.current
    );

    evaluationTimeoutRef.current =
      setTimeout(() => {
        evaluateAnswer();
      }, 4000);

    return () =>
      clearTimeout(
        evaluationTimeoutRef.current
      );

  }, [messages, answerDoc]);

  // ============================================
  // EVALUATE ANSWER
  // ============================================

  const evaluateAnswer = async () => {
    try {
      setEvaluating(true);

      // USER TRANSCRIPT

      const spokenTranscript =
        messages
          .filter(
            (msg) =>
              msg.role === "user"
          )
          .map((msg) => msg.text)
          .join(" ");

      // RECENT CONVERSATION

      const recentConversation =
        messages
          .slice(-8)
          .map(
            (m) =>
              `${m.role}: ${m.text}`
          )
          .join("\n");

      // PREVENT DUPLICATE EVALUATION

      const payloadKey =
        spokenTranscript +
        answerDoc +
        currentQuestion;

      if (
        payloadKey ===
        lastEvaluatedPayloadRef.current
      ) {
        return;
      }

      lastEvaluatedPayloadRef.current =
        payloadKey;

      // CALL API

      const response =
        await fetch(
          `${process.env.REACT_APP_BACKEND_BASE_URL}/api/interviews/evaluate-answer`,
          {
            method: "POST",

            headers: {
              "Content-Type":
                "application/json",
            },

            body: JSON.stringify({
              question:
                currentQuestion,

              writtenAnswer:
                answerDoc,

              spokenTranscript,

              recentConversation,
            }),
          }
        );

      const data =
        await response.json();

      console.log(
        "EVALUATION",
        data
      );

      setEvaluation(data);

      // ============================================
      // WEAK ANSWER → FOLLOWUP
      // ============================================

      if (
        data.score < 5 &&
        data.followupQuestion
      ) {
        await askQuestion(
          data.followupQuestion
        );

        return;
      }

      // ============================================
      // GOOD ANSWER → NEXT QUESTION
      // ============================================

      const nextIndex =
        questionIndex + 1;

      // INTERVIEW COMPLETE

      if (
        nextIndex >=
        QUESTION_BANK[
          interviewType
        ].length
      ) {
        await askQuestion(
          "Thank you Rohit. This concludes your RealHired interview."
        );

        return;
      }

      // NEXT QUESTION

      setQuestionIndex(nextIndex);

      const nextQuestion =
        QUESTION_BANK[
          interviewType
        ][nextIndex];

      setAnswerDoc("");

      await askQuestion(
        nextQuestion
      );

    } catch (err) {
      console.error(err);
    } finally {
      setEvaluating(false);
    }
  };

  // ============================================
  // FORMAT TIMER
  // ============================================

  const formattedTime = useMemo(() => {
    const mins = Math.floor(
      timeLeft / 60
    );

    const secs = timeLeft % 60;

    return `${String(mins).padStart(
      2,
      "0"
    )}:${String(secs).padStart(
      2,
      "0"
    )}`;
  }, [timeLeft]);

  // ============================================
  // LOADING
  // ============================================

  if (status === "loading") {
    return (
      <div className="min-h-screen bg-[#F8FAFC] flex items-center justify-center">
        <div className="flex flex-col items-center">
          <div className="relative">
            <div className="w-40 h-40 rounded-full bg-gradient-to-br from-violet-500 via-blue-400 to-cyan-300 blur-3xl opacity-20 absolute inset-0" />

            <div className="relative w-32 h-32 rounded-full bg-gradient-to-br from-violet-500 via-blue-400 to-cyan-300 animate-pulse" />
          </div>

          <h2 className="mt-10 text-2xl font-bold text-gray-900">
            Connecting AI Interviewer
          </h2>

          <p className="mt-3 text-gray-500">
            Preparing your interview
            workspace...
          </p>
        </div>
      </div>
    );
  }

  // ============================================
  // START SCREEN
  // ============================================

  if (status === "idle") {
    return (
      <div className="min-h-screen bg-[#F8FAFC] flex items-center justify-center p-6">
        <div className="w-full max-w-md bg-white rounded-3xl border border-gray-200 p-8 shadow-sm">
          <div className="flex items-center gap-3 mb-6">
            <div className="w-12 h-12 rounded-2xl bg-violet-100 flex items-center justify-center text-violet-600 font-bold text-xl">
              RH
            </div>

            <div>
              <h1 className="text-2xl font-bold text-gray-900">
                RealHired AI
              </h1>

              <p className="text-sm text-gray-500">
                Technical Interview
              </p>
            </div>
          </div>

          <div className="mb-5">
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Select Interview Type
            </label>

            <select
              value={
                interviewType
              }
              onChange={(e) =>
                setInterviewType(
                  e.target.value
                )
              }
              className="w-full h-12 rounded-xl border border-gray-300 px-4 outline-none"
            >
              <option value="">
                Choose interview type
              </option>

              {INTERVIEW_TYPES.map(
                (t) => (
                  <option
                    key={t.value}
                    value={t.value}
                  >
                    {t.label}
                  </option>
                )
              )}
            </select>
          </div>

          {error && (
            <div className="mb-4 rounded-xl bg-red-50 border border-red-200 p-3 text-sm text-red-600">
              {error}
            </div>
          )}

          <button
            onClick={
              startInterview
            }
            className="w-full h-12 rounded-xl bg-violet-600 text-white font-semibold"
          >
            Start Interview
          </button>
        </div>
      </div>
    );
  }

  // ============================================
  // MAIN UI
  // ============================================

  return (
    <div className="h-screen bg-[#F8FAFC] flex flex-col overflow-hidden">
      {/* HEADER */}

      <header className="h-20 bg-white border-b border-gray-200 px-6 flex items-center justify-between">
        <div className="flex items-center gap-4">
          <div className="w-11 h-11 rounded-2xl bg-violet-100 flex items-center justify-center text-violet-600 font-bold">
            RH
          </div>

          <div>
            <h1 className="text-lg font-semibold text-gray-900">
              RealHired AI
            </h1>

            <p className="text-sm text-gray-500">
              Adaptive Technical
              Interview
            </p>
          </div>
        </div>

        <div className="flex items-center gap-4">
          <div className="bg-white border border-gray-200 rounded-2xl px-5 py-3 flex items-center gap-3 shadow-sm">
            <div className="w-10 h-10 rounded-xl bg-orange-100 flex items-center justify-center text-orange-600">
              <Clock3 className="w-5 h-5" />
            </div>

            <div>
              <p className="text-xs text-gray-500">
                Free Time Left
              </p>

              <h3 className="font-bold text-lg text-gray-900">
                {formattedTime}
              </h3>
            </div>
          </div>

          <button className="h-12 px-5 rounded-xl bg-violet-600 text-white font-medium">
            Add Credits
          </button>
        </div>
      </header>

      {/* BODY */}

      <div className="flex-1 flex overflow-hidden">
        {/* CENTER */}

        <div className="flex-1 relative flex flex-col items-center justify-center px-10">
          <div className="flex items-center gap-24">
            {/* AI */}

            <div className="flex flex-col items-center">
              <div className="relative">
                <div className="w-72 h-72 rounded-full bg-gradient-to-br from-violet-500 via-blue-400 to-cyan-300 blur-3xl opacity-20 absolute inset-0" />

                <div className="relative w-64 h-64 rounded-full bg-gradient-to-br from-violet-500 via-blue-400 to-cyan-300 p-[3px]">
                  <div className="w-full h-full rounded-full bg-white flex items-center justify-center">
                    <div className="w-44 h-44 rounded-full bg-gradient-to-br from-violet-500 via-blue-400 to-cyan-300 animate-pulse" />
                  </div>
                </div>
              </div>

              <h2 className="mt-8 text-2xl font-bold text-gray-900">
                AI Interviewer
              </h2>

              <div className="mt-3 px-4 py-2 rounded-full bg-violet-100 text-violet-700 text-sm font-medium">
                {waitingForUser
                  ? "Waiting..."
                  : evaluating
                  ? "Analyzing..."
                  : "Listening"}
              </div>
            </div>

            {/* USER */}

            <div className="flex flex-col items-center">
              <div className="w-64 h-64 rounded-full bg-white border border-gray-200 shadow-lg flex items-center justify-center text-7xl font-bold text-violet-600">
                R
              </div>

              <h2 className="mt-8 text-2xl font-bold text-gray-900">
                Rohit
              </h2>

              <div className="mt-3 px-4 py-2 rounded-full bg-green-100 text-green-700 text-sm font-medium">
                Listening...
              </div>
            </div>
          </div>

          {/* CONTROLS */}

          <div className="mt-20 flex items-center gap-5">
            <button className="w-20 h-20 rounded-3xl bg-white border border-gray-200 shadow-sm flex flex-col items-center justify-center gap-2">
              <Mic className="w-6 h-6 text-gray-700" />

              <span className="text-sm font-medium">
                Mic
              </span>
            </button>

            <button className="w-20 h-20 rounded-3xl bg-white border border-gray-200 shadow-sm flex flex-col items-center justify-center gap-2">
              <Video className="w-6 h-6 text-gray-700" />

              <span className="text-sm font-medium">
                Camera
              </span>
            </button>

            <button className="w-20 h-20 rounded-3xl bg-white border border-gray-200 shadow-sm flex flex-col items-center justify-center gap-2">
              <Monitor className="w-6 h-6 text-gray-700" />

              <span className="text-sm font-medium">
                Share
              </span>
            </button>

            <button
              onClick={
                stopInterview
              }
              className="w-20 h-20 rounded-3xl bg-red-50 border border-red-200 shadow-sm flex flex-col items-center justify-center gap-2"
            >
              <PhoneOff className="w-6 h-6 text-red-500" />

              <span className="text-sm font-medium text-red-500">
                End
              </span>
            </button>
          </div>
        </div>

        {/* RIGHT PANEL */}

        <div
          className={`${
            collapsed
              ? "w-20"
              : "w-[480px]"
          } transition-all duration-300 bg-white border-l border-gray-200 flex flex-col relative`}
        >
          {/* COLLAPSE */}

          <button
            onClick={() =>
              setCollapsed(
                !collapsed
              )
            }
            className="absolute -left-4 top-6 w-8 h-8 rounded-full bg-white border border-gray-200 shadow flex items-center justify-center z-20"
          >
            {collapsed ? (
              <ChevronLeft className="w-4 h-4" />
            ) : (
              <ChevronRight className="w-4 h-4" />
            )}
          </button>

          {collapsed ? (
            <div className="flex flex-col items-center gap-6 pt-20">
              <button
                onClick={() => {
                  setCollapsed(
                    false
                  );

                  setActiveTab(
                    "chat"
                  );
                }}
                className="w-12 h-12 rounded-2xl bg-gray-100 flex items-center justify-center"
              >
                <MessageSquare className="w-5 h-5" />
              </button>

              <button
                onClick={() => {
                  setCollapsed(
                    false
                  );

                  setActiveTab(
                    "transcript"
                  );
                }}
                className="w-12 h-12 rounded-2xl bg-gray-100 flex items-center justify-center"
              >
                <ScrollText className="w-5 h-5" />
              </button>

              <button
                onClick={() => {
                  setCollapsed(
                    false
                  );

                  setActiveTab(
                    "doc"
                  );
                }}
                className="w-12 h-12 rounded-2xl bg-violet-100 flex items-center justify-center"
              >
                <FileText className="w-5 h-5 text-violet-600" />
              </button>
            </div>
          ) : (
            <>
              {/* TABS */}

              <div className="h-20 border-b border-gray-200 flex items-center px-6 gap-3">
                <button
                  onClick={() =>
                    setActiveTab(
                      "chat"
                    )
                  }
                  className={`px-4 h-11 rounded-xl text-sm font-medium ${
                    activeTab ===
                    "chat"
                      ? "bg-violet-100 text-violet-700"
                      : "text-gray-500"
                  }`}
                >
                  Chat
                </button>

                <button
                  onClick={() =>
                    setActiveTab(
                      "transcript"
                    )
                  }
                  className={`px-4 h-11 rounded-xl text-sm font-medium ${
                    activeTab ===
                    "transcript"
                      ? "bg-violet-100 text-violet-700"
                      : "text-gray-500"
                  }`}
                >
                  Transcript
                </button>

                <button
                  onClick={() =>
                    setActiveTab(
                      "doc"
                    )
                  }
                  className={`px-4 h-11 rounded-xl text-sm font-medium ${
                    activeTab ===
                    "doc"
                      ? "bg-violet-100 text-violet-700"
                      : "text-gray-500"
                  }`}
                >
                  Answer Doc
                </button>
              </div>

              {/* CHAT */}

              {activeTab ===
                "chat" && (
                <div className="flex-1 overflow-y-auto p-5 space-y-4">
                  {messages.map(
                    (
                      msg,
                      idx
                    ) => (
                      <div
                        key={idx}
                        className={`flex ${
                          msg.role ===
                          "assistant"
                            ? "justify-start"
                            : "justify-end"
                        }`}
                      >
                        <div
                          className={`max-w-[80%] rounded-2xl px-4 py-3 text-sm leading-relaxed ${
                            msg.role ===
                            "assistant"
                              ? "bg-gray-100 text-gray-700"
                              : "bg-violet-600 text-white"
                          }`}
                        >
                          {msg.text}
                        </div>
                      </div>
                    )
                  )}
                </div>
              )}

              {/* TRANSCRIPT */}

              {activeTab ===
                "transcript" && (
                <div className="flex-1 overflow-y-auto p-5 space-y-3">
                  {messages.map(
                    (
                      msg,
                      idx
                    ) => (
                      <div
                        key={idx}
                        className="text-sm text-gray-700"
                      >
                        <span className="font-semibold capitalize">
                          {msg.role}:
                        </span>{" "}
                        {msg.text}
                      </div>
                    )
                  )}
                </div>
              )}

              {/* DOC */}

              {activeTab ===
                "doc" && (
                <div className="flex-1 flex flex-col">
                  <div className="p-5 border-b border-gray-200">
                    <div className="bg-violet-50 border border-violet-100 rounded-2xl p-4">
                      <p className="text-xs uppercase tracking-wide text-violet-500 mb-2">
                        Current Question
                      </p>

                      <p className="text-sm font-medium text-violet-700 leading-relaxed">
                        {
                          currentQuestion
                        }
                      </p>
                    </div>
                  </div>

                  <textarea
                    value={
                      answerDoc
                    }
                    onChange={(
                      e
                    ) =>
                      setAnswerDoc(
                        e.target.value
                      )
                    }
                    placeholder="Write your answer here..."
                    className="flex-1 resize-none outline-none p-5 text-gray-700 leading-relaxed"
                  />

                  {/* EVALUATION */}

                  {evaluation && (
                    <div className="border-t border-gray-200 bg-gray-50 p-5">
                      <div className="flex items-center justify-between mb-3">
                        <h3 className="font-semibold text-gray-900">
                          Live
                          Evaluation
                        </h3>

                        <div className="px-3 py-1 rounded-full bg-violet-100 text-violet-700 text-sm font-semibold">
                          {
                            evaluation.score
                          }
                          /10
                        </div>
                      </div>

                      <p className="text-sm text-gray-600 leading-relaxed mb-4">
                        {
                          evaluation.feedback
                        }
                      </p>

                      <div className="space-y-2">
                        {evaluation.strengths?.map(
                          (
                            s,
                            index
                          ) => (
                            <div
                              key={
                                index
                              }
                              className="text-sm text-green-700"
                            >
                              ✓ {s}
                            </div>
                          )
                        )}

                        {evaluation.weaknesses?.map(
                          (
                            w,
                            index
                          ) => (
                            <div
                              key={
                                index
                              }
                              className="text-sm text-red-600"
                            >
                              ⚠ {w}
                            </div>
                          )
                        )}
                      </div>
                    </div>
                  )}
                </div>
              )}
            </>
          )}
        </div>
      </div>
    </div>
  );
}